import { captureMessage } from '@sentry/react';
import { ConfigState } from '../store/config';
import { SortDirection, TenantListSortOption } from './TenantUtils';

/** First path segment of every `defaultStartView` the app can render (routes in App.tsx). */
export const KNOWN_START_VIEWS: readonly string[] = [
  'tenants',
  'publictransit',
  'energy-declaration-ovk',
  'property-info',
  'restaurant-menu',
  'documents',
  'weather',
  'embedded-urls',
  'floor-plan',
  'services',
  'booking',
];

const TENANT_LIST_SORT_OPTIONS: readonly TenantListSortOption[] = [
  'name',
  'last_name',
  'floor',
];
const SORT_DIRECTIONS: readonly SortDirection[] = ['asc', 'desc'];

type ValidatedField = keyof Pick<
  ConfigState,
  | 'defaultTenantListSortOption'
  | 'defaultTenantListSortOrder'
  | 'defaultStartView'
  | 'tenantListColumns'
>;

/** Called once per config value that `normalizeConfig` replaced. */
export type ConfigValueReporter = (
  field: ValidatedField,
  value: unknown,
  replacement: unknown,
) => void;

const reportReplacedValue: ConfigValueReporter = (
  field,
  value,
  replacement,
) => {
  captureMessage(`Invalid device config value for ${field}`, {
    level: 'warning',
    extra: { field, value, replacement },
  });
};

const valueKey = (value: unknown): string => {
  try {
    return `${typeof value}:${JSON.stringify(value) ?? String(value)}`;
  } catch {
    return `${typeof value}:${String(value)}`;
  }
};

/**
 * Returns a reporter that sends each distinct (field, value) pair to Sentry
 * once. The config query refetches every hour, so without this a device with
 * one bad value would report the same message for as long as it stays up.
 */
export const createConfigValueReporter = (): ConfigValueReporter => {
  const reported = new Set<string>();
  return (field, value, replacement) => {
    const key = `${field}:${valueKey(value)}`;
    if (reported.has(key)) {
      return;
    }
    reported.add(key);
    reportReplacedValue(field, value, replacement);
  };
};

const isKnownStartView = (value: unknown): value is string =>
  typeof value === 'string' && KNOWN_START_VIEWS.includes(value.split('/')[0]);

/**
 * Replaces device config values that would crash or misbehave at render time
 * with the value from `defaults`. Valid config passes through unchanged and
 * the input is never mutated. The only side effect is one `report` call per
 * replaced value (by default a `captureMessage`), so bad device config is
 * visible in Sentry.
 */
export const normalizeConfig = (
  config: ConfigState,
  defaults: ConfigState,
  report: ConfigValueReporter = reportReplacedValue,
): ConfigState => {
  const normalized = { ...config };

  const sortOption: unknown = config.defaultTenantListSortOption;
  if (!TENANT_LIST_SORT_OPTIONS.includes(sortOption as TenantListSortOption)) {
    normalized.defaultTenantListSortOption =
      defaults.defaultTenantListSortOption;
    report(
      'defaultTenantListSortOption',
      sortOption,
      normalized.defaultTenantListSortOption,
    );
  }

  const sortOrder: unknown = config.defaultTenantListSortOrder;
  if (!SORT_DIRECTIONS.includes(sortOrder as SortDirection)) {
    normalized.defaultTenantListSortOrder = defaults.defaultTenantListSortOrder;
    report(
      'defaultTenantListSortOrder',
      sortOrder,
      normalized.defaultTenantListSortOrder,
    );
  }

  // Trailing slashes are trimmed so that useNavigateToDefaultView does not
  // read the empty last segment as an attachment or embedded-url id.
  const startView: unknown = config.defaultStartView;
  const trimmedStartView: unknown =
    typeof startView === 'string' ? startView.replace(/\/+$/, '') : startView;
  if (!isKnownStartView(trimmedStartView)) {
    normalized.defaultStartView = defaults.defaultStartView;
    report('defaultStartView', startView, normalized.defaultStartView);
  } else if (trimmedStartView !== startView) {
    normalized.defaultStartView = trimmedStartView;
    report('defaultStartView', startView, trimmedStartView);
  }

  const columns: unknown = config.tenantListColumns;
  const columnCount = Number(columns);
  normalized.tenantListColumns = columnCount === 1 ? 1 : 2;
  if (columnCount !== 1 && columnCount !== 2) {
    report('tenantListColumns', columns, normalized.tenantListColumns);
  }

  return normalized;
};
